import { hasFileExtension } from '~/utils/check-url-extension';
import { formatCanonicalUrl } from '~/utils/canonical';

interface IMetaResponse {
  title: string,
  description: string,
  publishedTime: string,
  modifiedTime: string,
  image: {
    url: string;
    width: number;
    height: number;
    alt?: string;
  }
  canonical?: string;
  isTax?: string;
}

export default defineNuxtPlugin((nuxtApp) => {
  const userStore = useUserStore();
  const router = useRouter()
  const meta = useRequestEvent()?.context?.meta;
  const runtimeConfig = useRuntimeConfig();
  const path = router.currentRoute.value.path.replace(/^\/+/, '');
  const type = (path.startsWith('product/') || path.startsWith('ms-my/product/')) ? 'product' : 'article';
  const { $file } = useNuxtApp();
  const pageSeoInfo = usePageSeoInfo();

  const getLocaleCode = () => {
    const event = useRequestEvent();
    const currentPath = event?.path || router.currentRoute.value?.path || '';
    const isMs = currentPath.startsWith('/ms-my') || currentPath.startsWith('ms-my') || (nuxtApp as any).$i18n?.locale?.value === 'ms-my' || (nuxtApp as any).$i18n?.locale === 'ms-my';
    return isMs ? 'ms_MY' : 'en_US';
  };

    const updateMetaTags = (meta: IMetaResponse) => {
      const rawCanonical = meta.canonical ? meta.canonical : (meta.isTax ? runtimeConfig.public.BASE_URL + meta.isTax : undefined);
      const localeCode = getLocaleCode();

      useHead({
        title: htmlSpecialcharsDecode(meta.title),
        link: [
          {
            rel: 'canonical',
            href: () => {
              const currentPath = router.currentRoute.value.path;
              const canonicalUrl = formatCanonicalUrl(
                rawCanonical,
                currentPath,
                pageSeoInfo.value,
                runtimeConfig.public.BASE_URL
              );
              return decodeURIComponent(canonicalUrl);
            }
          },
        ],
        meta: [
          { name: 'description', content: htmlSpecialcharsDecode(meta.description) },
          { property: 'robots', content: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1' },
          ...(meta.publishedTime ? [{
            property: 'article:published_time',
            content: meta.publishedTime
          }] : []),
          ...(meta.modifiedTime ? [{
            property: 'article:modified_time',
            content: meta.modifiedTime
          }] : []),
          ...(meta.modifiedTime ? [{
            property: 'og:updated_time',
            content: meta.modifiedTime
          }] : []),
          { property: 'og:title', content: htmlSpecialcharsDecode(meta.title) },
          { property: 'og:description', content: htmlSpecialcharsDecode(meta.description) },

          { property: 'dc:title', content: htmlSpecialcharsDecode(meta.title) },
          { property: 'dc:description', content: htmlSpecialcharsDecode(meta.description) },
          { property: 'dc:relation', content: runtimeConfig.public.BASE_URL + path },
          { property: 'dc:source', content: runtimeConfig.public.BASE_URL },
          { property: 'dc:language', content: localeCode },

          { property: 'og:title', content: htmlSpecialcharsDecode(meta.title) },
          { property: 'og:description', content: htmlSpecialcharsDecode(meta.description) },
          ...(meta.image && meta.image.url ? [{
            property: 'og:image',
            content: meta.image.url
          }] : []),
          ...(meta.image && meta.image.url ? [{
            property: 'og:image:secure_url',
            content: meta.image.url
          }] : []),
          ...(meta.image && meta.image.width ? [{
            property: 'og:image:width',
            content: meta.image.width
          }] : []),
          ...(meta.image && meta.image.height ? [{
            property: 'og:image:height',
            content: meta.image.height
          }] : []),
          { property: 'og:url', content: runtimeConfig.public.BASE_URL + path },
          { property: 'og:site_name', content: 'SK Jewellery' },
          { property: 'og:locale', content: localeCode },
          { property: 'og:type', content: type },

          { property: 'fb:pages', content: '' },
          { property: 'fb:admins', content: '' },
          { property: 'fb:app_id', content: '' },

          { property: 'article:author', content: '' },
          { property: 'article:publisher', content: '' },

          { property: 'twitter:card', content: '' },
          { property: 'twitter:site', content: '' },
          { property: 'twitter:creator', content: '' },
          { property: 'twitter:title', content: htmlSpecialcharsDecode(meta.title) },
          { property: 'twitter:description', content: htmlSpecialcharsDecode(meta.description) },
          { property: 'google-site-verification', content: '6zZydxkm0n4LNztRv3ZoLdK4TcjCZmOv-31I7Ehh7pM' },
          { property: 'facebook-domain-verification', content: 'mcle4utzw6vf0l7t0pp7ujhtqc1gq7' },
          { property: 'msapplication-TileImage', content: 'https://www.skjewellery.com.my/wp-content/uploads/2020/06/cropped-cropped-sk_logo_512-1-270x270.png' },

        ]
      })
    }

    if (meta) {
      updateMetaTags(meta);
    }

    router.beforeEach((to) => {
      pageSeoInfo.value = null;
      const originalPath = to.path;

      if( userStore.socialRequired && ! originalPath.startsWith('/enter-password') ) {
        return navigateTo('/enter-password/');
      }
    });

    router.afterEach(async (to) => {
      const originalPath = to.path;
      let path = originalPath.split('?')[0];
      
      // Extract country code if present
      let $country = '';
      if (path.startsWith('/ms-my') || path.startsWith('ms-my')) {
        $country = 'ms-my';
        path = path.replace(/^\/ms-my/, '').replace(/^ms-my/, '');
        if (!path || path === '') {
          path = '/';
        }
      }

      if (hasFileExtension(path)) {
        return;
      }

      const exludePaths = ['/404/'];
      if( exludePaths.includes(path) ) {
        return;
      }

      const arrTax =
        [
          'collection/',
          'product-category/',
          'metal-color/',
          'product-price-range/',
          'product-bridal/',
          'product-recipients/',
          'product-gold-type/',
          'product-occasions/',
          'product-advanced/',
          'product-gifts/',
          'shop/'
        ]
      const eventPath = path.startsWith('/') ? path.slice(1) : path;
      const cleanPath = eventPath.endsWith('/') ? eventPath.slice(0, -1) : eventPath;
      const pathWithoutParams = cleanPath.split("/").filter(Boolean);

      let currentPath = path === '/' ? 'home-page' : cleanPath;
      if (arrTax.some(tax => currentPath.startsWith(tax))) {
        currentPath = `${pathWithoutParams.slice(0, 2).join("/")}`;
      }
      if (currentPath.startsWith('my-account/')) {
        currentPath = 'my-account';
      } else if (currentPath.startsWith('wishlist/')) {
        currentPath = 'wishlist';
      }

      try {
        const meta = await $fetch<IMetaResponse>(`/api/data/meta/${currentPath.toLowerCase().replace(/%/g, '')}.json`, {
          headers: {
            Authorization: `Bearer ${userStore.csrf}`,
          },
        })

        updateMetaTags(meta)
      } catch (error) {
        console.error('Failed to fetch meta:', error)
      }

    });
})